import React, { useEffect, useState } from "react";
import { useInvalidator, useResource } from "@rest-hooks/core";
import {
	LoginResource,
	PartnerWebAgentResource
} from "@ploy-lib/rest-resources";
import {
	parseQueryStringParams,
	bootstrapQueryStringForAgentSetup
} from "./bootstrap-query-string-parsing";
import { useEventManager } from "@ploy-lib/calculation/es/react/context";
import { apiResourceUrl } from "@ploy-lib/core";
import { DefaultLinearProgress, ErrorPage } from "@ploy-ui/core";
import { CssBaseline } from "@material-ui/core";
import { HiddenInProductionTenantSelector } from "@ploy-ui/tenants";
import { useSearchParams, createSearchParams } from "react-router-dom";

function filterInt(value?: string) {
	if (value != null && /^[-+]?(\d+)$/.test(value)) {
		return Number(value);
	} else {
		return NaN;
	}
}

interface AgentParams {
	agent?: string;
	token?: string;
	vendorId?: number;
	isCookieless?: boolean;
	skipCookiesGatekeeper?: boolean
}

export function getAgentParams(): AgentParams {
	const queryString = window.location.search;

	const params = parseQueryStringParams(queryString);

	if (!(params && (params.agent || params.token))) return {};

	const possibleVendorId = filterInt(params.vendorId);
	const isCookieless = !params.token && params.cookieless === "cookieless" ? true : undefined
	return {
		agent: params.agent,
		token: params.token,
		vendorId: isNaN(possibleVendorId) ? undefined : possibleVendorId,
		isCookieless: isCookieless,
		skipCookiesGatekeeper: params.skipCookiesGatekeeper || isCookieless
	};
}

export function AuthenticateQueryAgent(props: { children: React.ReactNode }) {
	const { token, ...agentParams } = getAgentParams();

	const [waitForTokenAccess, setWaitForTokenAccess] = useState(token != null);
	const [failedTokenAccess, setFailedTokenAccess] = useState(false);
	const [responseOnError, setResponseOnError] = useState<Response | null>(null);
	const [previousToken, setPreviousToken] = useState<string | null>(null);

	var partnerResult = useResource(
		PartnerWebAgentResource.detail(),
		token == null && agentParams.agent != null ? agentParams : null
	);

	const loginStatusInvalidator = useInvalidator(LoginResource.status());
	useEffect(() => {
		if (partnerResult != null) loginStatusInvalidator({});
	}, [loginStatusInvalidator, partnerResult]);
	const [params, setParams] = useSearchParams();

	useEffect(() => {
		const doFetch = async () => {
			const url = apiResourceUrl("partnerWeb");
			try {
				const response = await fetch(url, {
					method: "POST",
					cache: "no-cache",
					credentials: "include",
					headers: {
						Authorization: "Bearer " + token
					},
					redirect: "error"
				});

				if (
					response.ok &&
					response.headers.get("content-type")?.split(";")[0].trim() ===
						"application/json"
				) {
					await response.json();
					setWaitForTokenAccess(false);
				} else {
					setResponseOnError(response);
					setFailedTokenAccess(true);
				}
			} catch (e) {
				setFailedTokenAccess(true);
			}

			const newParams = createSearchParams(params);
			newParams.delete("ext.token");
			setParams(newParams, { replace: true });
		};

		if (typeof token === "string" && token !== previousToken) {
			setFailedTokenAccess(false);
			setWaitForTokenAccess(true);
			setPreviousToken(token);
			doFetch();
		} else if (token != null && typeof token !== "string") {
			setFailedTokenAccess(true);
		}
	}, [previousToken, token, params, setParams]);

	if (failedTokenAccess) {
		console.log("failed Token access");
		return (
			<>
				<ErrorPage error={{ response: responseOnError }} />
				<CssBaseline />
				<HiddenInProductionTenantSelector />
			</>
		);
	}

	if (waitForTokenAccess)
		return (
			<>
				{params.get("hideLinearProgressBar") === "true" ? null : (
					<DefaultLinearProgress />
				)}
				<CssBaseline />
				<HiddenInProductionTenantSelector />
			</>
		);

	return <>{props.children}</>;
}

export function BootstrapQueryEvents(props: { children: React.ReactNode }) {
	const eventProvider = useEventManager();

	if (getAgentParams()) bootstrapQueryStringForAgentSetup(eventProvider);

	return <>{props.children}</>;
}
